// components/business/BusinessCardDesktop.tsx

import { buildPublicBusinessCardUrl } from "@/lib/config/brand";
import { formatItemPrice } from "@/lib/itemPricing";
import type { Business } from "@/lib/types";
import { useBusinessOpenStatus } from "@/lib/useBusinessOpenStatus";
import { buildWhatsAppCta } from "@/lib/whatsapp";
import {
  Bookmark,
  Briefcase,
  Copy,
  Download,
  Flag,
  Info,
  MapPin,
  Navigation,
  Phone,
  Share2,
  ShoppingBag,
  type LucideIcon,
} from "lucide-react-native";
import React, { useMemo } from "react";
import {
    Alert,
    Animated,
    Easing,
    Image,
    Linking,
    Platform,
    Pressable,
    ScrollView,
    StyleSheet,
    Text,
    View,
} from "react-native";
import AboutUsPopup from "./AboutUsPopup";
import BusinessReportModal from "./BusinessReportModal";
import ItemImageViewerModal from "./ItemImageViewerModal";
import type { ItemImageViewerEntry } from "./ItemImageViewerModal";
import SocialIcon from "./SocialIcon";
import { usePublicBusinessCardActions } from "./usePublicBusinessCardActions";
import type { BusinessCardActionHandler } from "./usePublicBusinessCardActions";

type Props = {
  business: Business;
  onShare?: BusinessCardActionHandler;
  onSave?: BusinessCardActionHandler;
  onDownload?: BusinessCardActionHandler;
};

type SocialLink = {
  key: string;
  label: string;
  url: string;
  platform: string;
};

type BusinessModePreview = {
  title: string;
  subtitle: string;
  Icon: LucideIcon;
};

type ItemImageGalleryEntry = ItemImageViewerEntry & {
  itemIndex: number;
};

function safeText(v: unknown) {
  return String(v ?? "").trim();
}

function normalizeKey(value: string) {
  return value.toLowerCase().replace(/\s+/g, " ").trim();
}

function splitCommaParts(value: string) {
  return value
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
}

function uniqueCleanParts(parts: string[]) {
  const seen = new Set<string>();

  return parts.filter((part) => {
    const value = safeText(part);
    const key = normalizeKey(value);

    if (!key) return false;
    if (seen.has(key)) return false;

    seen.add(key);
    return true;
  });
}

function removeCountyParts(parts: string[], countyName?: string | null) {
  const county = safeText(countyName);
  const countyKey = normalizeKey(county);
  const countyWithoutWordKey = normalizeKey(county.replace(/\s+county$/i, ""));

  return parts.filter((part) => {
    const value = safeText(part);
    const key = normalizeKey(value);

    if (!key) return false;
    if (key.endsWith(" county")) return false;
    if (countyKey && key === countyKey) return false;
    if (countyWithoutWordKey && key === countyWithoutWordKey) return false;

    return true;
  });
}

function getAreaCityLabel(business: Business) {
  const b = business as any;
  const loc = b.location || {};

  const areaName = safeText(
    loc.areaName ||
      loc.areaSuggestionName ||
      b.areaName ||
      b.areaSuggestionName
  );

  const cityName = safeText(
    loc.cityName ||
      loc.citySuggestionName ||
      b.cityName ||
      b.citySuggestionName
  );

  const countyName = safeText(loc.countyName || b.countyName);
  const cityLine = safeText(b.cityLine);

  const directParts = uniqueCleanParts([areaName, cityName]);
  const cityLineParts = removeCountyParts(splitCommaParts(cityLine), countyName);

  const finalParts = uniqueCleanParts([...directParts, ...cityLineParts]);

  return finalParts.slice(0, 2).join(", ");
}

function getBusinessTypeDisplayLabel(businessType: unknown) {
  if (businessType === "online_only") return "Online Shop";
  if (businessType === "freelancer") return "Freelancer";
  return "";
}

function getBusinessModePreview(
  businessType: unknown
): BusinessModePreview | null {
  if (businessType === "online_only") {
    return {
      title: "Online Shop",
      subtitle: "Online orders available",
      Icon: ShoppingBag,
    };
  }

  if (businessType === "freelancer") {
    return {
      title: "Freelancer",
      subtitle: "Services/products at client location",
      Icon: Briefcase,
    };
  }

  return null;
}

function hasValidCoordinates(lat: unknown, lng: unknown) {
  return (
    typeof lat === "number" &&
    Number.isFinite(lat) &&
    typeof lng === "number" &&
    Number.isFinite(lng)
  );
}

function buildLocationDisplay(business: Business) {
  const b = business as any;

  const businessType = b.businessType;
  const businessName = safeText(b.name);
  const addressLineRaw = safeText(b.addressLine);
  const areaCity = getAreaCityLabel(business);
  const typeLabel = getBusinessTypeDisplayLabel(businessType);

  const addressLine =
    addressLineRaw && normalizeKey(addressLineRaw) !== normalizeKey(businessName)
      ? addressLineRaw
      : "";

  const lat = b.locationPin?.lat;
  const lng = b.locationPin?.lng;
  const hasCoords = hasValidCoordinates(lat, lng);

  const isPhysicalShop = businessType === "physical_shop";
  const isOnlineOrFreelance =
    businessType === "online_only" || businessType === "freelancer";

  if (isOnlineOrFreelance) {
    return {
      line1: typeLabel,
      line2: areaCity,
      areaCity,
      mapAddress: areaCity,
      mapTitle: businessName || typeLabel,
      mapSubtitle: areaCity,
      canOpenMap: false,
      showMapPreview: false,
      hasLocationText: !!typeLabel || !!areaCity,
    };
  }

  const physicalLine1 = addressLine || areaCity || "Business location";
  const physicalLine2 = addressLine ? areaCity : "";

  return {
    line1: physicalLine1,
    line2: physicalLine2,
    areaCity,
    mapAddress: addressLine || areaCity,
    mapTitle: businessName || "Business location",
    mapSubtitle: addressLine ? areaCity : "",
    canOpenMap: isPhysicalShop && hasCoords,
    showMapPreview: isPhysicalShop && hasCoords,
    hasLocationText: !!physicalLine1 || !!physicalLine2 || hasCoords,
  };
}

function normalizeWebUrl(url?: string | null) {
  const u = safeText(url);
  if (!u) return "";

  if (/^(https?:\/\/|tel:|mailto:|sms:|whatsapp:)/i.test(u)) {
    return u;
  }

  return `https://${u}`;
}

function normalizeEmailUrl(value?: string | null) {
  const raw = safeText(value);
  if (!raw) return "";

  if (/^mailto:/i.test(raw)) return raw;

  const email = raw.replace(/^mailto:/i, "").trim();
  if (!email) return "";

  return `mailto:${email}`;
}

function normalizePhoneForTel(phone?: string | null) {
  const raw = safeText(phone);
  if (!raw) return "";

  const cleaned = raw.replace(/[^\d+]/g, "");

  if (/^0\d{9}$/.test(cleaned)) {
    return `+254${cleaned.slice(1)}`;
  }

  if (/^[17]\d{8}$/.test(cleaned)) {
    return `+254${cleaned}`;
  }

  if (/^254\d{9}$/.test(cleaned)) {
    return `+${cleaned}`;
  }

  return cleaned;
}

function normalizeSocialUrl(
  value?: string | null,
  platform?:
    | "facebook"
    | "instagram"
    | "linkedin"
    | "twitter"
    | "tiktok"
    | "youtube"
) {
  const raw = safeText(value);
  if (!raw) return "";

  if (/^https?:\/\//i.test(raw)) return raw;

  const cleaned = raw.replace(/^@/, "").replace(/^\/+/, "");

  if (raw.includes(".")) {
    return `https://${raw}`;
  }

  if (platform === "facebook") return `https://facebook.com/${cleaned}`;
  if (platform === "instagram") return `https://instagram.com/${cleaned}`;
  if (platform === "linkedin") return `https://linkedin.com/in/${cleaned}`;
  if (platform === "twitter") return `https://x.com/${cleaned}`;
  if (platform === "tiktok") return `https://tiktok.com/@${cleaned}`;

  if (platform === "youtube") {
    if (
      cleaned.startsWith("c/") ||
      cleaned.startsWith("channel/") ||
      cleaned.startsWith("user/")
    ) {
      return `https://youtube.com/${cleaned}`;
    }

    return `https://youtube.com/@${cleaned}`;
  }

  return normalizeWebUrl(raw);
}

async function openUrl(url?: string | null) {
  const u = safeText(url);
  if (!u) return;

  try {
    await Linking.openURL(u);
  } catch (e) {
    console.log("openUrl failed:", u, e);
    Alert.alert(
      "Could not open link",
      "Please check that the link or phone number is valid."
    );
  }
}

function joinCategories(b: Business) {
  const cats =
    (b as any).categoryName || (b as any).categories || (b as any).category;

  const sub =
    (b as any).subcategoryName ??
    (b as any).subCategory ??
    (b as any).subcategory;

  const subCats = (b as any).subCategories ?? (b as any).subcategories;

  const catText = Array.isArray(cats)
    ? cats.filter(Boolean).join(" · ")
    : safeText(cats);

  const subText = Array.isArray(subCats)
    ? subCats.filter(Boolean).join(" · ")
    : Array.isArray(sub)
    ? sub.filter(Boolean).join(" · ")
    : safeText(sub || subCats);

  return [catText, subText].filter(Boolean).join(" · ");
}

function stars(rating?: number) {
  const r = Math.max(0, Math.min(5, Number(rating || 0)));
  const full = Math.floor(r);
  const empty = 5 - full;

  return "★".repeat(full) + "☆".repeat(empty);
}

function getItemImage(it: any) {
  const candidates = [
    it?.imageUrl,
    it?.imageURI,
    it?.imageUri,
    it?.img,
    it?.image,
    it?.photoUrl,
  ];

  for (const c of candidates) {
    const t = safeText(c);
    if (t) return t;
  }

  return "";
}

function getImageResizeMode(value: unknown): "cover" | "contain" {
  return value === "contain" ? "contain" : "cover";
}

function getDomainLabel(url: string) {
  return url
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .replace(/\/+$/g, "")
    .trim();
}

function buildGoogleMapsSearchUrl(lat: number, lng: number) {
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
}

function buildStaticMapPreviewUrl(lat?: number | null, lng?: number | null) {
  const mapKey = safeText(process.env.EXPO_PUBLIC_GOOGLE_MAPS_STATIC_KEY);

  if (!mapKey) return "";
  if (!hasValidCoordinates(lat, lng)) return "";

  const center = `${lat},${lng}`;
  const marker = encodeURIComponent(`color:red|${center}`);

  return `https://maps.googleapis.com/maps/api/staticmap?center=${center}&zoom=16&size=640x320&scale=2&maptype=roadmap&markers=${marker}&key=${encodeURIComponent(
    mapKey
  )}`;
}

function AutoScrollingText({
  text,
  textStyle,
}: {
  text: string;
  textStyle: any;
}) {
  const translateX = React.useRef(new Animated.Value(0)).current;
  const animationRef = React.useRef<Animated.CompositeAnimation | null>(null);

  const [containerWidth, setContainerWidth] = React.useState(0);
  const [textWidth, setTextWidth] = React.useState(0);

  const shouldScroll = textWidth > containerWidth + 4;

  React.useEffect(() => {
    animationRef.current?.stop();
    translateX.setValue(0);

    if (!shouldScroll || containerWidth <= 0 || textWidth <= 0) {
      return;
    }

    const distance = textWidth - containerWidth + 36;
    const duration = Math.max(12000, distance * 55);

    const animation = Animated.loop(
      Animated.sequence([
        Animated.delay(900),
        Animated.timing(translateX, {
          toValue: -distance,
          duration,
          easing: Easing.linear,
          useNativeDriver: true,
        }),
        Animated.delay(700),
        Animated.timing(translateX, {
          toValue: 0,
          duration: 1,
          easing: Easing.linear,
          useNativeDriver: true,
        }),
      ])
    );

    animationRef.current = animation;
    animation.start();

    return () => {
      animation.stop();
    };
  }, [containerWidth, shouldScroll, text, textWidth, translateX]);

  return (
    <View
      style={styles.marqueeOuter}
      onLayout={(event) => {
        setContainerWidth(event.nativeEvent.layout.width);
      }}
    >
      <Animated.View
        style={[
          styles.marqueeInner,
          shouldScroll ? { transform: [{ translateX }] } : null,
        ]}
      >
        <Text
          style={textStyle}
          numberOfLines={1}
          onLayout={(event) => {
            setTextWidth(event.nativeEvent.layout.width);
          }}
        >
          {text}
        </Text>
      </Animated.View>
    </View>
  );
}

function DesktopLocationPinMap({
  lat,
  lng,
  canOpenMap,
}: {
  lat?: number | null;
  lng?: number | null;
  canOpenMap?: boolean;
}) {
  const staticMapUrl = buildStaticMapPreviewUrl(lat, lng);
  const [imageFailed, setImageFailed] = React.useState(false);

  React.useEffect(() => {
    setImageFailed(false);
  }, [staticMapUrl]);

  const hasCoords = hasValidCoordinates(lat, lng);
  const disabled = !hasCoords || !canOpenMap;

  const mapsUrl =
    hasCoords && typeof lat === "number" && typeof lng === "number"
      ? buildGoogleMapsSearchUrl(lat, lng)
      : "";

  const showStaticMap = !!staticMapUrl && !imageFailed;

  return (
    <Pressable
      disabled={disabled}
      onPress={() => openUrl(mapsUrl)}
      style={({ pressed }) => [
        styles.mapPressable,
        pressed && !disabled ? styles.mapCardPressed : null,
      ]}
    >
      {showStaticMap ? (
        <Image
          source={{ uri: staticMapUrl }}
          style={styles.mapImage}
          resizeMode="cover"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <View style={styles.mapFallbackBackground}>
          <View style={[styles.mapGridLineH, { top: "26%" }]} />
          <View style={[styles.mapGridLineH, { top: "52%" }]} />
          <View style={[styles.mapGridLineH, { top: "78%" }]} />

          <View style={[styles.mapGridLineV, { left: "25%" }]} />
          <View style={[styles.mapGridLineV, { left: "52%" }]} />
          <View style={[styles.mapGridLineV, { left: "78%" }]} />

          <View style={styles.mapRoadHorizontal} />
          <View style={styles.mapRoadVertical} />
        </View>
      )}

      <View style={styles.mapPinWrap}>
        <View style={styles.mapPinCircle}>
          <MapPin size={25} color="#ffffff" strokeWidth={2.5} />
        </View>
      </View>

      <View style={styles.mapFooter}>
        <Navigation
          size={16}
          color={disabled ? "#64748b" : "#2563eb"}
          strokeWidth={2.5}
        />

        <Text
          style={[
            styles.mapFooterHint,
            disabled ? styles.mapFooterHintDisabled : null,
          ]}
          numberOfLines={1}
        >
          {disabled ? "Location preview" : "Click to open in Google Maps"}
        </Text>
      </View>
    </Pressable>
  );
}

function BusinessModePreviewCard({ preview }: { preview: BusinessModePreview }) {
  const Icon = preview.Icon;

  return (
    <View style={styles.modePreviewCard}>
      <View style={styles.modeIconCircle}>
        <Icon size={30} color="#ffffff" strokeWidth={2.5} />
      </View>

      <Text style={styles.modeTitle} numberOfLines={1}>
        {preview.title}
      </Text>

      <Text style={styles.modeSubtitle} numberOfLines={3}>
        {preview.subtitle}
      </Text>
    </View>
  );
}

export default function BusinessCardDesktop({
  business,
  onShare,
  onSave,
  onDownload,
}: Props) {
  const cardRef = React.useRef<View>(null);
  const [aboutVisible, setAboutVisible] = React.useState(false);
  const [viewerInitialIndex, setViewerInitialIndex] = React.useState<
    number | null
  >(null);
  const {
    saved,
    handleShare,
    handleCopyLink,
    handleSave,
    handleDownload,
    handleReport,
    reportModalProps,
  } = usePublicBusinessCardActions({
    business,
    cardRef,
    onShare,
    onSave,
    onDownload,
  });
  const isWeb = Platform.OS === "web";
  const openStatus = useBusinessOpenStatus(business);

  const name = safeText((business as any).name);
  const logoUrl = safeText((business as any).logoUrl);
  const logoFit = getImageResizeMode((business as any).logoFit || "contain");

  const rating = Number((business as any).rating || 0);
  const reviewCount = Number((business as any).reviewCount || 0);

  const rawPhone = safeText((business as any).phone);
  const phoneTel = normalizePhoneForTel(rawPhone);
  const phoneLabel = phoneTel || rawPhone;
  const shortDescription = safeText((business as any).shortDescription);

  const website = normalizeWebUrl(
    (business as any).website || (business as any).websiteUrl
  );

  const websiteLabel = website ? getDomainLabel(website) : "—";

  const locationDisplay = useMemo(
    () => buildLocationDisplay(business),
    [business]
  );

  const businessModePreview = useMemo(
    () => getBusinessModePreview((business as any).businessType),
    [business]
  );

  const whatsappCta = buildWhatsAppCta({
    whatsappCtaType: (business as any).whatsappCtaType,
    whatsappChatNumber: (business as any).whatsappChatNumber,
    whatsappCatalogUrl: (business as any).whatsappCatalogUrl,
  });

  const itemsTitle = safeText((business as any).itemsTitle) || "Our Products";

  const items = useMemo<any[]>(
    () =>
      Array.isArray((business as any).items) ? (business as any).items : [],
    [business]
  );

  const itemImageGallery = useMemo<ItemImageGalleryEntry[]>(() => {
    const entries: ItemImageGalleryEntry[] = [];

    items.forEach((it, idx) => {
      const uri = getItemImage(it);
      if (!uri) return;

      const title = safeText(it?.name || it?.title || `Item ${idx + 1}`);
      const description = safeText(it?.description);
      const priceText = formatItemPrice(it);

      entries.push({
        key: `${idx}-${uri}`,
        itemIndex: idx,
        title,
        uri,
        priceText,
        description,
      });
    });

    return entries;
  }, [items]);

  const openItemImageViewer = React.useCallback(
    (itemIndex: number) => {
      const galleryIndex = itemImageGallery.findIndex(
        (item) => item.itemIndex === itemIndex
      );

      if (galleryIndex >= 0) {
        setViewerInitialIndex(galleryIndex);
      }
    },
    [itemImageGallery]
  );

  const closeItemImageViewer = React.useCallback(() => {
    setViewerInitialIndex(null);
  }, []);

  const socialLinks = useMemo<SocialLink[]>(() => {
    const email = normalizeEmailUrl(
      (business as any).email || (business as any).businessEmail
    );

    const facebook = normalizeSocialUrl(
      (business as any).facebook || (business as any).facebookUrl,
      "facebook"
    );

    const instagram = normalizeSocialUrl(
      (business as any).instagram || (business as any).instagramUrl,
      "instagram"
    );

    const tiktok = normalizeSocialUrl(
      (business as any).tiktok || (business as any).tiktokUrl,
      "tiktok"
    );

    const linkedin = normalizeSocialUrl(
      (business as any).linkedin ||
        (business as any).linkedIn ||
        (business as any).linkedinUrl ||
        (business as any).linkedInUrl,
      "linkedin"
    );

    const youtube = normalizeSocialUrl(
      (business as any).youtube || (business as any).youtubeUrl,
      "youtube"
    );

    const twitter = normalizeSocialUrl(
      (business as any).twitter ||
        (business as any).x ||
        (business as any).xUrl ||
        (business as any).twitterUrl,
      "twitter"
    );

    return [
      { key: "email", label: "Email", url: email, platform: "email" },
      {
        key: "facebook",
        label: "Facebook",
        url: facebook,
        platform: "facebook",
      },
      {
        key: "instagram",
        label: "Instagram",
        url: instagram,
        platform: "instagram",
      },
      {
        key: "tiktok",
        label: "TikTok",
        url: tiktok,
        platform: "tiktok",
      },
      {
        key: "linkedin",
        label: "LinkedIn",
        url: linkedin,
        platform: "linkedin",
      },
      {
        key: "youtube",
        label: "YouTube",
        url: youtube,
        platform: "youtube",
      },
      { key: "twitter", label: "X", url: twitter, platform: "twitter" },
    ].filter((item) => !!item.url);
  }, [business]);

  const BottomPrimaryIcon = isWeb ? Copy : Share2;
  const BottomLastIcon = isWeb ? Flag : Download;

  const websiteTextLimit =
    socialLinks.length >= 3 ? 18 : socialLinks.length >= 1 ? 24 : 34;

  const showWebsiteIconOnly =
    !!website &&
    (socialLinks.length >= 4 || websiteLabel.length > websiteTextLimit);

  const categoriesLine = useMemo(() => joinCategories(business), [business]);

  const slug = safeText((business as any).slug);
  const qrTarget = website || buildPublicBusinessCardUrl(slug);
  const qrData = encodeURIComponent(qrTarget || "");
  const qrUrl = qrData
    ? `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${qrData}`
    : "";

  const locationPin = (business as any).locationPin;
  const hasContactActions = !!shortDescription || !!phoneTel;

  return (
    <View style={styles.cardWrap}>
      <View ref={cardRef} collapsable={false} style={styles.cardOuter}>
        <View style={styles.contentGrid}>
        <View style={styles.leftCol}>
          <View style={styles.headerRow}>
            <View style={styles.logoWrap}>
              {logoUrl ? (
                <Image
                  source={{ uri: logoUrl }}
                  style={styles.logo}
                  resizeMode={logoFit}
                />
              ) : (
                <View style={[styles.logo, styles.logoFallback]}>
                  <Text style={styles.logoFallbackText}>
                    {(name || "B").slice(0, 1).toUpperCase()}
                  </Text>
                </View>
              )}
            </View>

            <View style={styles.headerInfo}>
              <Text style={styles.name} numberOfLines={2}>
                {name || "Business Name"}
              </Text>

              {!!categoriesLine && (
                <AutoScrollingText
                  text={categoriesLine}
                  textStyle={styles.categories}
                />
              )}

              <View style={styles.ratingRow}>
                <Text style={styles.stars}>{stars(rating)}</Text>
                <Text style={styles.ratingText}>
                  {rating ? rating.toFixed(1) : "0.0"} ({reviewCount || 0})
                </Text>
              </View>

              <View style={styles.openRow}>
                <View
                  style={[
                    styles.dot,
                    openStatus.tone === "open"
                      ? styles.dotGreen
                      : openStatus.tone === "closed"
                      ? styles.dotRed
                      : styles.dotGray,
                  ]}
                />

                <Text style={styles.openText} numberOfLines={1}>
                  {openStatus.fullText}
                </Text>
              </View>
            </View>
          </View>

          <View style={styles.contactBlock}>
            {locationDisplay.hasLocationText ? (
              <View style={styles.addressBlock}>
                <View style={styles.addressMainRow}>
                  <View style={styles.addressIconCircle}>
                    <MapPin size={15} color="#e11d48" strokeWidth={2.5} />
                  </View>

                  <View style={styles.addressLines}>
                    <Text style={styles.addressText} numberOfLines={1}>
                      {locationDisplay.line1}
                    </Text>

                    {!!locationDisplay.line2 && (
                      <Text style={styles.addressTextMuted} numberOfLines={1}>
                        {locationDisplay.line2}
                      </Text>
                    )}
                  </View>
                </View>
              </View>
            ) : null}

            {hasContactActions ? (
              <View style={styles.primaryActionsRow}>
                {!!shortDescription && (
                  <Pressable
                    onPress={() => setAboutVisible(true)}
                    style={({ pressed }) => [
                      styles.aboutBtn,
                      pressed && styles.pressed,
                    ]}
                    accessibilityLabel="Open About Us"
                  >
                    <Info size={16} color="#111827" strokeWidth={2.5} />
                    <Text style={styles.aboutBtnText}>About Us</Text>
                  </Pressable>
                )}

                {!!phoneTel && (
                  <Pressable
                    onPress={() => openUrl(`tel:${phoneTel}`)}
                    style={({ pressed }) => [
                      styles.callBtn,
                      !shortDescription && styles.callBtnSolo,
                      pressed && styles.pressed,
                    ]}
                    accessibilityLabel={`Call ${phoneLabel}`}
                  >
                    <Phone size={16} color="#111827" strokeWidth={2.5} />
                    <Text style={styles.callBtnText} numberOfLines={1}>
                      {phoneLabel}
                    </Text>
                  </Pressable>
                )}
              </View>
            ) : null}

            <View style={styles.webSocialRow}>
              {!!website && (
                <Pressable
                  onPress={() => openUrl(website)}
                  style={
                    showWebsiteIconOnly
                      ? styles.websiteIconOnly
                      : styles.websiteInline
                  }
                  accessibilityLabel="Website"
                >
                  <SocialIcon
                    platform="website"
                    url={website}
                    size={16}
                    color="#2563eb"
                  />

                  {!showWebsiteIconOnly && (
                    <Text style={styles.linkText} numberOfLines={1}>
                      {websiteLabel}
                    </Text>
                  )}
                </Pressable>
              )}

              <View style={styles.socialRowInline}>
                {socialLinks.map((item) => (
                  <Pressable
                    key={item.key}
                    onPress={() => openUrl(item.url)}
                    style={styles.socialBtnSmall}
                    accessibilityLabel={item.label}
                  >
                    <SocialIcon
                      platform={item.platform}
                      url={item.url}
                      size={15}
                      color="#111827"
                    />
                  </Pressable>
                ))}
              </View>
            </View>
          </View>

          {!!whatsappCta.url && (
            <Pressable
              onPress={() => openUrl(whatsappCta.url)}
              style={({ pressed }) => [
                styles.whatsappBtn,
                pressed && styles.pressed,
              ]}
            >
              <View style={styles.whatsappIcon}>
                <SocialIcon
                  platform="whatsapp"
                  url={whatsappCta.url}
                  size={18}
                  color="#ffffff"
                />
              </View>
              <Text style={styles.whatsappText}>{whatsappCta.label}</Text>
            </Pressable>
          )}

          <View style={styles.qrMapRow}>
            <View style={styles.qrCard}>
              {qrUrl ? (
                <Image source={{ uri: qrUrl }} style={styles.qrImage} />
              ) : (
                <View style={[styles.qrImage, styles.qrFallback]}>
                  <Text style={styles.qrFallbackText}>QR</Text>
                </View>
              )}

              <Text style={styles.qrHint}>Scan to view on phone</Text>
            </View>

            <View style={styles.mapCardWrap}>
              {locationDisplay.showMapPreview ? (
                <DesktopLocationPinMap
                  lat={locationPin?.lat ?? null}
                  lng={locationPin?.lng ?? null}
                  canOpenMap={locationDisplay.canOpenMap}
                />
              ) : businessModePreview ? (
                <BusinessModePreviewCard preview={businessModePreview} />
              ) : (
                <View style={styles.locationInfoCard}>
                  <View style={styles.locationInfoIcon}>
                    <MapPin size={22} color="#ffffff" strokeWidth={2.5} />
                  </View>

                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={styles.locationInfoTitle} numberOfLines={1}>
                      {locationDisplay.line1 || "Location"}
                    </Text>

                    {!!locationDisplay.line2 && (
                      <Text
                        style={styles.locationInfoSubtitle}
                        numberOfLines={2}
                      >
                        {locationDisplay.line2}
                      </Text>
                    )}

                    <Text style={styles.locationInfoHint}>
                      Location preview unavailable
                    </Text>
                  </View>
                </View>
              )}
            </View>
          </View>
        </View>

        <View style={styles.rightCol}>
          <Text style={styles.productsTitle}>{itemsTitle}</Text>

          <View style={styles.productsScrollFrame}>
            <ScrollView
              style={styles.productsScroll}
              contentContainerStyle={styles.productsScrollInner}
              showsVerticalScrollIndicator
              nestedScrollEnabled
            >
              {items.length ? (
                items.map((it, idx) => {
                  const title = safeText(
                    it?.name || it?.title || `Item ${idx + 1}`
                  );
                  const img = getItemImage(it);
                  const imageFit = getImageResizeMode(it?.imageFit);

                  return (
                    <View key={`${title}-${idx}`} style={styles.itemCard}>
                      {img ? (
                        <Pressable
                          onPress={() => openItemImageViewer(idx)}
                          style={({ pressed }) => [
                            pressed ? styles.itemImagePressed : null,
                          ]}
                          accessibilityRole="button"
                          accessibilityLabel={`Open ${title} image`}
                        >
                          <Image
                            source={{ uri: img }}
                            style={styles.itemImage}
                            resizeMode={imageFit}
                          />
                        </Pressable>
                      ) : (
                        <View
                          style={[
                            styles.itemImage,
                            styles.itemImageFallback,
                          ]}
                        >
                          <Text style={styles.itemImageFallbackText}>IMG</Text>
                        </View>
                      )}

                      <View style={styles.itemFooter}>
                        <Text numberOfLines={1} style={styles.itemTitle}>
                          {title}
                        </Text>
                      </View>
                    </View>
                  );
                })
              ) : (
                <View style={styles.emptyProducts}>
                  <Text style={styles.emptyText}>
                    No products/services added yet.
                  </Text>
                </View>
              )}
            </ScrollView>
          </View>
        </View>
      </View>

        <View style={styles.bottomBar}>
          <Pressable
            onPress={isWeb ? handleCopyLink : handleShare}
            style={({ pressed }) => [
              styles.bottomBtn,
              pressed && styles.pressed,
            ]}
          >
            <BottomPrimaryIcon size={16} color="#111827" strokeWidth={2.5} />
            <Text style={styles.bottomBtnText}>
              {isWeb ? "Copy Link" : "Share"}
            </Text>
          </Pressable>

          <Pressable
            onPress={handleSave}
            style={({ pressed }) => [
              styles.bottomBtn,
              pressed && styles.pressed,
            ]}
          >
            <Bookmark
              size={16}
              color="#111827"
              fill={saved ? "#111827" : "none"}
              strokeWidth={2.5}
            />
            <Text style={styles.bottomBtnText}>{saved ? "Saved" : "Save"}</Text>
          </Pressable>

          <Pressable
            onPress={isWeb ? handleReport : handleDownload}
            style={({ pressed }) => [
              styles.bottomBtn,
              pressed && styles.pressed,
            ]}
          >
            <BottomLastIcon size={16} color="#111827" strokeWidth={2.5} />
            <Text style={styles.bottomBtnText}>
              {isWeb ? "Report" : "Download"}
            </Text>
          </Pressable>
        </View>
      </View>
      <BusinessReportModal {...reportModalProps} />
      <AboutUsPopup
        visible={aboutVisible}
        shortDescription={shortDescription}
        onClose={() => setAboutVisible(false)}
      />
      <ItemImageViewerModal
        visible={viewerInitialIndex !== null}
        items={itemImageGallery}
        initialIndex={viewerInitialIndex ?? 0}
        onClose={closeItemImageViewer}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  cardWrap: {
    width: "100%",
    flex: 1,
    minHeight: 0,
  },

  cardOuter: {
    width: "100%",
    flex: 1,
    minHeight: 0,
    borderRadius: 22,
    backgroundColor: "#fff",
    overflow: "hidden",
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.08)",
  },

  contentGrid: {
    flex: 1,
    flexDirection: "row",
    minHeight: 0,
  },

  leftCol: {
    flex: 65,
    padding: 14,
    borderRightWidth: 1,
    borderRightColor: "rgba(0,0,0,0.08)",
    minHeight: 0,
  },

  rightCol: {
    flex: 35,
    padding: 14,
    minHeight: 0,
  },

  headerRow: {
    flexDirection: "row",
    alignItems: "flex-start",
  },

  logoWrap: {
    width: 82,
    height: 82,
    borderRadius: 16,
    overflow: "hidden",
    backgroundColor: "rgba(0,0,0,0.03)",
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.08)",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },

  logo: {
    width: 82,
    height: 82,
  },

  logoFallback: {
    alignItems: "center",
    justifyContent: "center",
  },

  logoFallbackText: {
    fontSize: 30,
    fontWeight: "900",
    color: "rgba(0,0,0,0.55)",
  },

  headerInfo: {
    flex: 1,
    minWidth: 0,
  },

  name: {
    fontSize: 18,
    fontWeight: "900",
    color: "#111827",
    marginBottom: 4,
  },

  marqueeOuter: {
    width: "100%",
    overflow: "hidden",
    marginBottom: 6,
  },

  marqueeInner: {
    alignSelf: "flex-start",
  },

  categories: {
    fontSize: 13,
    fontWeight: "700",
    color: "rgba(17,24,39,0.55)",
  },

  ratingRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 6,
  },

  stars: {
    fontSize: 13,
    color: "#111827",
    marginRight: 8,
  },

  ratingText: {
    fontSize: 12,
    color: "rgba(17,24,39,0.65)",
    fontWeight: "700",
  },

  openRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 6,
  },

  dot: {
    width: 9,
    height: 9,
    borderRadius: 999,
    marginRight: 8,
  },

  dotGreen: {
    backgroundColor: "#16a34a",
  },

  dotRed: {
    backgroundColor: "#ef4444",
  },

  dotGray: {
    backgroundColor: "rgba(0,0,0,0.25)",
  },

  openText: {
    fontSize: 13,
    color: "rgba(17,24,39,0.8)",
    fontWeight: "700",
  },

  contactBlock: {
    marginTop: 2,
  },

  addressBlock: {
    marginTop: 6,
    marginBottom: 8,
  },

  addressMainRow: {
    flexDirection: "row",
    alignItems: "flex-start",
  },

  addressIconCircle: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: "#fff1f2",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
    marginTop: 1,
  },

  addressLines: {
    flex: 1,
    minWidth: 0,
  },

  addressText: {
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "900",
    color: "#111827",
  },

  addressTextMuted: {
    marginTop: 2,
    fontSize: 12,
    lineHeight: 17,
    fontWeight: "700",
    color: "#64748b",
  },

  contactRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 6,
  },

  contactIconWrap: {
    width: 20,
    marginRight: 8,
    alignItems: "center",
    justifyContent: "center",
  },

  contactText: {
    fontSize: 14,
    fontWeight: "800",
    color: "#111827",
  },

  primaryActionsRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    marginTop: 8,
    marginBottom: 8,
  },

  aboutBtn: {
    flex: 1,
    minWidth: 0,
    minHeight: 32,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 6,
  },

  aboutBtnText: {
    fontSize: 13,
    fontWeight: "900",
    color: "#111827",
  },

  callBtn: {
    flex: 1,
    minWidth: 0,
    minHeight: 32,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 6,
  },

  callBtnSolo: {
    flex: 0,
    justifyContent: "flex-start",
  },

  callBtnText: {
    flexShrink: 1,
    fontSize: 13,
    fontWeight: "900",
    color: "#111827",
  },

  webSocialRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },

  websiteInline: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
    marginRight: 10,
  },

  websiteIconOnly: {
    width: 30,
    height: 30,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "rgba(37,99,235,0.18)",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#eff6ff",
    marginRight: 8,
  },

  linkText: {
    flex: 1,
    minWidth: 0,
    marginLeft: 6,
    fontSize: 14,
    fontWeight: "900",
    color: "#2563eb",
  },

  socialRowInline: {
    flexDirection: "row",
    alignItems: "center",
  },

  socialBtnSmall: {
    width: 30,
    height: 30,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.12)",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
    marginLeft: 8,
  },

  whatsappBtn: {
    marginTop: 10,
    borderRadius: 14,
    backgroundColor: "#1f8f5a",
    paddingVertical: 10,
    paddingHorizontal: 14,
    flexDirection: "row",
    alignItems: "center",
  },

  whatsappIcon: {
    width: 22,
    marginRight: 8,
  },

  whatsappText: {
    color: "white",
    fontWeight: "900",
    fontSize: 16,
  },

  qrMapRow: {
    marginTop: 10,
    flexDirection: "row",
    alignItems: "stretch",
  },

  qrCard: {
    flex: 4,
    borderRadius: 16,
    padding: 8,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.08)",
    backgroundColor: "rgba(0,0,0,0.02)",
    alignItems: "center",
    marginRight: 8,
  },

  qrImage: {
    width: "100%",
    aspectRatio: 1,
    maxWidth: 110,
    maxHeight: 110,
    borderRadius: 12,
    backgroundColor: "#fff",
  },

  qrFallback: {
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.1)",
  },

  qrFallbackText: {
    fontSize: 18,
    fontWeight: "900",
    color: "rgba(0,0,0,0.45)",
  },

  qrHint: {
    marginTop: 6,
    fontSize: 10,
    fontWeight: "800",
    color: "rgba(17,24,39,0.55)",
  },

  mapCardWrap: {
    flex: 6,
    minHeight: 148,
  },

  mapPressable: {
    flex: 1,
    minHeight: 148,
    borderRadius: 18,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: "#cbd5e1",
    backgroundColor: "#e0f2fe",
  },

  mapCardPressed: {
    opacity: 0.9,
    transform: [{ scale: 0.99 }],
  },

  mapImage: {
    ...StyleSheet.absoluteFillObject,
    width: "100%",
    height: "100%",
  },

  mapFallbackBackground: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "#e0f2fe",
  },

  mapGridLineH: {
    position: "absolute",
    left: 0,
    right: 0,
    height: 1,
    backgroundColor: "rgba(15,23,42,0.08)",
  },

  mapGridLineV: {
    position: "absolute",
    top: 0,
    bottom: 0,
    width: 1,
    backgroundColor: "rgba(15,23,42,0.08)",
  },

  mapRoadHorizontal: {
    position: "absolute",
    left: -20,
    right: -20,
    top: "48%",
    height: 18,
    backgroundColor: "rgba(255,255,255,0.75)",
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: "rgba(15,23,42,0.08)",
    transform: [{ rotate: "-8deg" }],
  },

  mapRoadVertical: {
    position: "absolute",
    top: -25,
    bottom: -25,
    left: "55%",
    width: 20,
    backgroundColor: "rgba(255,255,255,0.68)",
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: "rgba(15,23,42,0.08)",
    transform: [{ rotate: "14deg" }],
  },

  mapPinWrap: {
    position: "absolute",
    top: "42%",
    left: "50%",
    width: 44,
    height: 44,
    marginLeft: -22,
    marginTop: -32,
    alignItems: "center",
    justifyContent: "center",
  },

  mapPinCircle: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: "#dc2626",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 3,
    borderColor: "#ffffff",
    shadowColor: "#000",
    shadowOpacity: 0.22,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
    elevation: 5,
  },

  mapFooter: {
    position: "absolute",
    left: 12,
    right: 12,
    bottom: 10,
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: "rgba(255,255,255,0.95)",
    borderWidth: 1,
    borderColor: "rgba(37,99,235,0.18)",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },

  mapFooterHint: {
    marginLeft: 6,
    fontSize: 11,
    fontWeight: "900",
    color: "#2563eb",
  },

  mapFooterHintDisabled: {
    color: "#64748b",
  },

  modePreviewCard: {
    flex: 1,
    minHeight: 148,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: "#dbeafe",
    backgroundColor: "#f8fafc",
    paddingHorizontal: 14,
    paddingVertical: 14,
    alignItems: "center",
    justifyContent: "center",
  },

  modeIconCircle: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: "#0f172a",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 10,
  },

  modeTitle: {
    fontSize: 16,
    lineHeight: 20,
    fontWeight: "900",
    color: "#0f172a",
    textAlign: "center",
  },

  modeSubtitle: {
    marginTop: 5,
    fontSize: 12,
    lineHeight: 17,
    fontWeight: "800",
    color: "#475569",
    textAlign: "center",
    maxWidth: 160,
  },

  locationInfoCard: {
    flex: 1,
    minHeight: 180,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: "#cbd5e1",
    backgroundColor: "#f8fafc",
    padding: 14,
    flexDirection: "row",
    alignItems: "center",
  },

  locationInfoIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#0f172a",
    marginRight: 12,
    alignItems: "center",
    justifyContent: "center",
  },

  locationInfoTitle: {
    fontSize: 16,
    fontWeight: "900",
    color: "#0f172a",
  },

  locationInfoSubtitle: {
    marginTop: 4,
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "700",
    color: "#475569",
  },

  locationInfoHint: {
    marginTop: 8,
    fontSize: 11,
    fontWeight: "800",
    color: "#64748b",
  },

  productsTitle: {
    fontSize: 18,
    fontWeight: "900",
    color: "#111827",
    marginBottom: 10,
  },

  productsScrollFrame: {
    flex: 1,
    minHeight: 0,
    height: 560,
    borderRadius: 14,
  },

  productsScroll: {
    flex: 1,
    minHeight: 0,
  },

  productsScrollInner: {
    paddingBottom: 10,
  },

  itemCard: {
    borderRadius: 16,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.08)",
    backgroundColor: "#fff",
    marginBottom: 10,
  },

  itemImage: {
    width: "100%",
    height: 160,
    backgroundColor: "rgba(0,0,0,0.04)",
  },

  itemImagePressed: {
    opacity: 0.9,
  },

  itemImageFallback: {
    alignItems: "center",
    justifyContent: "center",
  },

  itemImageFallbackText: {
    fontSize: 12,
    fontWeight: "900",
    color: "rgba(0,0,0,0.35)",
  },

  itemFooter: {
    padding: 10,
    borderTopWidth: 1,
    borderTopColor: "rgba(0,0,0,0.06)",
  },

  itemTitle: {
    fontSize: 14,
    fontWeight: "900",
    color: "#111827",
  },

  emptyProducts: {
    padding: 14,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.08)",
    backgroundColor: "rgba(0,0,0,0.02)",
  },

  emptyText: {
    fontSize: 12,
    fontWeight: "800",
    color: "rgba(17,24,39,0.55)",
  },

  bottomBar: {
    flexDirection: "row",
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: "rgba(0,0,0,0.08)",
    backgroundColor: "#fff",
  },

  bottomBtn: {
    flex: 1,
    borderRadius: 14,
    paddingVertical: 11,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.10)",
    backgroundColor: "rgba(0,0,0,0.02)",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
  },

  bottomBtnText: {
    fontSize: 13,
    fontWeight: "900",
    color: "#111827",
  },

  pressed: {
    opacity: 0.85,
    transform: [{ scale: 0.99 }],
  },
});
